import { useEffect, useMemo, useState } from "react";
import { api, API } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Download, Mail, Loader2, FileSpreadsheet } from "lucide-react";

const PRESETS = [
    { key: 7, label: "7 D" },
    { key: 30, label: "30 D" },
    { key: 90, label: "90 D" },
    { key: "lifetime", label: "Lifetime" },
    { key: "custom", label: "Custom" },
];

export default function ReportModal({ stationId, ownerEmail, plantName, onClose }) {
    // Selected preset — number (days) | "lifetime" | "custom"
    const [preset, setPreset] = useState(30);
    // Custom range state (used only when preset === "custom")
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState(new Date().toISOString().slice(0, 10));
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [downloadingCsv, setDownloadingCsv] = useState(false);
    const [downloadingPdf, setDownloadingPdf] = useState(false);
    const [email, setEmail] = useState(ownerEmail || "");
    const [subject, setSubject] = useState(`RPPL Solar · Performance Report · ${plantName}`);
    const [message, setMessage] = useState(`Dear Customer,\n\nPlease find attached the performance report for your solar installation.\n\nRegards,\nRPPL Solar Operations`);

    // Build the API payload once — reused by preview, PDF, CSV, and email.
    const payload = useMemo(() => {
        if (preset === "lifetime") return { station_id: stationId, days: 30, lifetime: true };
        if (preset === "custom") return { station_id: stationId, days: 30, start_date: startDate || undefined, end_date: endDate || undefined };
        return { station_id: stationId, days: preset };
    }, [stationId, preset, startDate, endDate]);

    // Custom range is only "ready" once both dates are set.
    const rangeReady = preset !== "custom" || (startDate && endDate);

    useEffect(() => {
        if (!rangeReady) return;
        setLoading(true);
        api.post("/reports/preview", payload)
            .then(r => setPreview(r.data))
            .catch(() => toast.error("Failed to load preview"))
            .finally(() => setLoading(false));
    }, [payload, rangeReady]);

    const downloadPdf = async () => {
        setDownloadingPdf(true);
        try {
            const res = await fetch(`${API}/reports/pdf`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const blob = await res.blob();
            downloadBlob(blob, `RPPL_${plantName.replace(/[^a-z0-9]/gi, "_")}_${tagFor(preset)}.pdf`);
            toast.success("PDF downloaded");
        } catch (e) {
            toast.error("PDF download failed");
        } finally { setDownloadingPdf(false); }
    };

    const downloadCsv = async () => {
        setDownloadingCsv(true);
        try {
            const res = await fetch(`${API}/reports/csv`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const blob = await res.blob();
            downloadBlob(blob, `RPPL_${plantName.replace(/[^a-z0-9]/gi, "_")}_${tagFor(preset)}.csv`);
            toast.success("CSV downloaded");
        } catch (e) {
            toast.error("CSV download failed");
        } finally { setDownloadingCsv(false); }
    };

    const sendEmail = async () => {
        setSending(true);
        try {
            const { data } = await api.post("/reports/email", { ...payload, to_email: email, subject, message });
            toast.success(data.message);
            onClose();
        } catch (e) {
            toast.error("Failed to queue email");
        } finally { setSending(false); }
    };

    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto rounded-sm" data-testid="report-modal">
                <DialogHeader>
                    <DialogTitle className="text-lg font-semibold tracking-tight">Performance Report Preview</DialogTitle>
                </DialogHeader>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
                    {/* Preview */}
                    <div className="border border-zinc-200 rounded-sm bg-[#F8F9FA] p-6 max-h-[65vh] overflow-y-auto" data-testid="report-preview">
                        {!rangeReady ? (
                            <div className="text-sm text-zinc-500">Pick a start and end date to preview a custom range.</div>
                        ) : loading || !preview ? (
                            <div className="text-sm text-zinc-500">Building preview…</div>
                        ) : (
                            <>
                                <div className="border-b-2 border-[#0A0A0A] pb-3 mb-4">
                                    <div className="text-2xl font-bold text-[#0A0A0A] tracking-wider">RPPL <span className="text-[#16A34A]">SOLAR</span></div>
                                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">Solar PV Monitoring System</div>
                                </div>
                                <div className="mb-4">
                                    <div className="text-lg font-semibold text-zinc-900">{preview.plant.name}</div>
                                    <div className="text-xs text-zinc-500 mt-0.5">{preview.plant.address}</div>
                                    <div className="text-xs font-mono text-zinc-500 mt-1">
                                        Period: {preview.period.start} → {preview.period.end} · {preview.period.days} days
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
                                    <MetricBox label="Total Generation" value={preview.summary.total_generation_kwh.toLocaleString()} unit="kWh" accent="green" />
                                    <MetricBox label="Average Daily" value={preview.summary.average_daily_kwh.toLocaleString()} unit="kWh" />
                                    <MetricBox label="CO₂ Avoided" value={preview.summary.co2_avoided_kg.toLocaleString()} unit="kg" accent="green" />
                                    <MetricBox label="Specific Yield" value={preview.summary.specific_yield_kwh_per_kw} unit="kWh/kWp" />
                                    <MetricBox label="Capacity" value={preview.plant.capacity_kw} unit="kWp" />
                                    <MetricBox
                                        label="System Uptime"
                                        value={preview.summary.uptime_pct != null ? preview.summary.uptime_pct : "—"}
                                        unit={preview.summary.uptime_pct != null ? "%" : ""}
                                        accent={preview.summary.uptime_pct != null && preview.summary.uptime_pct >= 90 ? "green" : preview.summary.uptime_pct != null && preview.summary.uptime_pct >= 70 ? "orange" : undefined}
                                        testid="report-uptime-box"
                                    />
                                </div>
                                <div className="mb-4">
                                    <div className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-2">Weather During Period</div>
                                    {preview.weather && preview.weather.source === "openweather" ? (
                                        (() => {
                                            const byDesc = preview.weather.by_description || {};
                                            const rows = Object.entries(byDesc).sort((a, b) => b[1] - a[1]);
                                            return rows.length ? (
                                                <div data-testid="weather-summary" className="rounded-lg border border-zinc-200 overflow-hidden">
                                                    <div className="grid grid-cols-[1fr_auto] text-[10px] uppercase tracking-widest bg-zinc-50 text-zinc-500 px-3 py-1.5 font-semibold">
                                                        <span>Condition</span><span>Days</span>
                                                    </div>
                                                    {rows.map(([desc, count]) => (
                                                        <div
                                                            key={desc}
                                                            data-testid={`weather-row-${desc.toLowerCase().replace(/\s+/g, "-")}`}
                                                            className="grid grid-cols-[1fr_auto] px-3 py-1.5 text-xs border-t border-zinc-100"
                                                        >
                                                            <span className="text-zinc-800">{desc}</span>
                                                            <span className="font-mono text-zinc-700 tabular-nums">{count}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="text-[11px] text-zinc-500 italic" data-testid="weather-empty">
                                                    No weather observations recorded for this period yet.
                                                </div>
                                            );
                                        })()
                                    ) : (
                                        <div className="text-[11px] text-zinc-500 italic" data-testid="weather-unavailable">
                                            Weather data unavailable{preview.weather?.note ? ` — ${preview.weather.note}` : ""}.
                                        </div>
                                    )}
                                    {preview.weather?.source === "openweather" && (
                                        <div className="text-[10px] text-zinc-400 mt-2 italic">
                                            Descriptions come directly from OpenWeather — days marked <span className="font-medium">Misty</span>, <span className="font-medium">Overcast Clouds</span>, or <span className="font-medium">Broken Clouds</span> explain any dip in production.
                                        </div>
                                    )}
                                    {preview.weather?.cached && (
                                        <div className="text-[10px] text-zinc-400 mt-1 font-mono">served from cache</div>
                                    )}
                                </div>
                                <div className="mb-4">
                                    <div className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-2">Inverter Fleet ({preview.inverters.length})</div>
                                    <table className="w-full text-xs">
                                        <thead><tr className="border-b border-zinc-200 text-zinc-500">
                                            <th className="py-1.5 text-left font-semibold">Model</th><th className="text-left font-semibold">Serial</th><th className="text-right font-semibold">kW</th>
                                        </tr></thead>
                                        <tbody>
                                            {preview.inverters.map(i => (
                                                <tr key={i.serial} className="border-b border-zinc-100">
                                                    <td className="py-1.5 text-zinc-800">{i.model}</td>
                                                    <td className="font-mono text-zinc-600">{i.serial}</td>
                                                    <td className="text-right font-mono text-zinc-700">{i.rated_kw != null ? i.rated_kw.toFixed(1) : "—"}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {(preview.data_loggers || []).length > 0 && (
                                        <div className="mt-2 pt-2 border-t border-dashed border-zinc-200">
                                            <div className="text-[9px] font-semibold uppercase tracking-widest text-zinc-400 mb-1">Data Loggers · sub-component</div>
                                            {preview.data_loggers.map(l => (
                                                <div key={l.serial} className="flex items-center justify-between text-[11px]">
                                                    <span className="text-zinc-600">{l.model}</span>
                                                    <span className="font-mono text-zinc-500">{l.serial}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div className="text-[10px] text-zinc-400 pt-3 border-t border-zinc-200 font-mono">
                                    Generated {preview.generated_at?.slice(0,10)} by {preview.generated_by} · RPPL Solar
                                </div>
                            </>
                        )}
                    </div>

                    {/* Email form */}
                    <div className="space-y-4">
                        <div>
                            <label className="text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-1.5 block">Period</label>
                            <div className="inline-flex flex-wrap rounded-sm border border-zinc-200 overflow-hidden">
                                {PRESETS.map(p => {
                                    const active = preset === p.key;
                                    return (
                                        <button
                                            key={p.key}
                                            onClick={() => setPreset(p.key)}
                                            data-testid={`period-${p.key}`}
                                            className={`px-3 py-1.5 text-xs uppercase font-medium border-r border-zinc-200 last:border-r-0 ${active ? "bg-[#0A0A0A] text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}
                                        >
                                            {p.label}
                                        </button>
                                    );
                                })}
                            </div>
                            {preset === "custom" && (
                                <div className="flex flex-wrap gap-3 mt-3" data-testid="custom-range">
                                    <div>
                                        <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1 block">Start</label>
                                        <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="rounded-sm h-9" data-testid="report-start-date" max={endDate || undefined} />
                                    </div>
                                    <div>
                                        <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1 block">End</label>
                                        <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="rounded-sm h-9" data-testid="report-end-date" min={startDate || undefined} max={new Date().toISOString().slice(0, 10)} />
                                    </div>
                                </div>
                            )}
                            {preset === "lifetime" && (
                                <div className="text-[11px] text-zinc-500 mt-2" data-testid="lifetime-hint">
                                    Covers every day from the plant's commissioning until today.
                                </div>
                            )}
                        </div>
                        <div>
                            <label className="text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-1.5 block">Customer email</label>
                            <Input value={email} onChange={e => setEmail(e.target.value)} data-testid="report-email-to" className="rounded-sm" />
                        </div>
                        <div>
                            <label className="text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-1.5 block">Subject</label>
                            <Input value={subject} onChange={e => setSubject(e.target.value)} data-testid="report-email-subject" className="rounded-sm" />
                        </div>
                        <div>
                            <label className="text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-1.5 block">Message</label>
                            <Textarea value={message} onChange={e => setMessage(e.target.value)} rows={5} data-testid="report-email-message" className="rounded-sm" />
                        </div>
                        <div className="flex flex-wrap gap-3 pt-2">
                            <Button variant="outline" onClick={downloadPdf} disabled={downloadingPdf || !rangeReady} className="rounded-sm h-10 flex-1 min-w-[130px]" data-testid="report-download-btn">
                                {downloadingPdf ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                                PDF
                            </Button>
                            <Button variant="outline" onClick={downloadCsv} disabled={downloadingCsv || !rangeReady} className="rounded-sm h-10 flex-1 min-w-[130px]" data-testid="report-download-csv-btn">
                                {downloadingCsv ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />}
                                CSV
                            </Button>
                            <Button onClick={sendEmail} disabled={sending || !email || !rangeReady} className="bg-[#0A0A0A] hover:bg-black text-white rounded-full h-10 flex-1 min-w-[150px]" data-testid="report-email-send-btn">
                                {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                                Email
                            </Button>
                        </div>
                        <div className="text-[10px] text-zinc-400 leading-relaxed">
                            Emails deliver via Resend with automatic retry on transient gateway errors. Weather counts (sunny/cloudy/rainy) come from OpenWeather and are cached in the DB.
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function tagFor(preset) {
    if (preset === "lifetime") return "lifetime";
    if (preset === "custom") return "custom_range";
    return `${preset}d`;
}

function downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function MetricBox({ label, value, unit, accent = "black", testid }) {
    const c = { orange: "text-[#0A0A0A]", green: "text-[#16A34A]", black: "text-zinc-900" }[accent];
    return (
        <div className="bg-white border border-zinc-200 rounded-sm p-3" data-testid={testid}>
            <div className="text-[9px] font-semibold uppercase tracking-widest text-zinc-500">{label}</div>
            <div className="mt-1 flex items-baseline gap-1">
                <span className={`text-lg font-medium font-mono ${c}`}>{value}</span>
                {unit && <span className="text-[10px] font-mono text-zinc-500">{unit}</span>}
            </div>
        </div>
    );
}
